/**
 * Client workspace · Onboarding (Foundation) — Task 049 + Task 088 Phase 1.
 *
 * Single-page Client Foundation workspace at
 * `/app/clients/:clientId/onboarding`. Replaces the multi-step wizard
 * (Task 049) with one scrollable workspace where every section is
 * always visible and editable inline. The mental model is "one page,
 * many sections" — closer to a Notion document than a step-by-step
 * form.
 *
 * Why the change (Task 088 product direction):
 *   - The client foundation must be excellent — it's generated once
 *     per agency client and feeds every downstream artifact (strategy,
 *     calendar, campaigns).
 *   - A multi-step wizard hides which fields are still empty and
 *     forces unnecessary clicks. One page makes gaps obvious and
 *     edits cheap.
 *   - Strategy generation should depend on an APPROVED foundation;
 *     this page is where the user reviews + approves.
 *
 * Read: `get-client-overview` + `get-client-onboarding-profile`.
 * Write:
 *   - `upsert-client-brand-info` (basics section)
 *   - `upsert-client-onboarding-profile` (identity / market / products /
 *     competitors / review sections, PATCH semantics)
 *   - `create-client-target-audience` (market section — adds new
 *     audience rows)
 *   - `create-client-goal` (goals section — adds new goal rows; moved
 *     here from Overview in Task 162)
 *   - `extract-client-onboarding-from-website` (AI extraction banner)
 *   - `apply-client-onboarding-suggestion` (called internally after
 *     extraction with `overwrite_existing: false` — fills empty fields
 *     in place; Task 163 removed the user-facing review surface)
 *
 * Permission policy (UX gate; RLS is the load-bearing security gate):
 *   - `client.manage` OR `client.self.manage` → full edit access.
 *   - Anyone else with workspace access → read-only "review" view.
 *
 * The page does NOT cover initiatives. Goals are managed here (Task 162
 * relocated them from Overview so audience + goals — the inputs the
 * server-side `enforceReadiness` gate cares about — sit next to each
 * other in the foundation workspace). The "missing fields" panel near
 * the top makes gaps visible and scrollable; the URL `?step=<key>`
 * query param scrolls to the matching section anchor (kept for
 * backward compat with bookmarks / "edit this section" deep links).
 */

import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { isApiError, isNetworkError, KmiApiError } from '../../api/client';
import {
  applyClientOnboardingSuggestion,
  createClientGoal,
  createClientTargetAudience,
  getClientOnboardingProfile,
  getClientOverview,
  upsertClientBrandInfo,
  upsertClientOnboardingProfile,
} from '../../api/endpoints';
import {
  useOnboardingExtractionJob,
  type OnboardingExtractionPhase,
} from './useOnboardingExtractionJob';
import type {
  ApplyClientOnboardingSuggestionData,
  ApplyProfileField,
  BrandInfo,
  ClientGoal,
  ClientOnboardingProfile,
  ClientOverviewData,
  OnboardingCompetitorItem,
  OnboardingProductServiceItem,
  TargetAudience,
} from '../../api/types';
import { useMeContext } from '../../context/MeContextProvider';
import { hasAnyPermission, PERM } from '../../permissions';
import { ErrorState, LoadingState, NotFoundState } from '../../components/states';

// Task 088 Phase 1: STEP_KEYS used to be a runtime array (for the
// stepper's `STEP_KEYS.includes()` URL-param validation). With the
// one-page design that runtime check is gone — only the union type
// remains. The literal-type form below is the smallest declaration
// that keeps the StepKey union without an unused runtime const.
type StepKey = 'basics' | 'identity' | 'market' | 'products' | 'competitors' | 'goals' | 'review';

interface StepDef {
  readonly key: StepKey;
  readonly index: number;
  readonly label: string;
  readonly hint: string;
}

const STEPS: ReadonlyArray<StepDef> = [
  {
    key: 'basics',
    index: 1,
    label: 'الأساسيات',
    hint: 'هوية مختصرة: الاسم، الموقع، القطاع، اللغة.',
  },
  {
    key: 'identity',
    index: 2,
    label: 'الهوية والصوت',
    hint: 'الوصف بالعربي والإنجليزي، الرؤية، الرسالة، القيم، USP.',
  },
  {
    key: 'market',
    index: 3,
    label: 'السوق',
    hint: 'الجمهور المستهدف، نقاط القوة والضعف، العروض الموسمية.',
  },
  { key: 'products', index: 4, label: 'المنتجات', hint: 'حتى 10 منتجات أو خدمات بنبذة موجزة.' },
  {
    key: 'competitors',
    index: 5,
    label: 'المنافسون',
    hint: 'قائمة منافسين يدوية للآن. الاكتشاف الذكي قريباً.',
  },
  // Task 162 — goals lives in Onboarding because audience + goals feed
  // Strategy/AI readiness (`enforceReadiness` server-side requires
  // ≥1 client_goals row). Placed after competitors and before review so
  // the review block sees a complete foundation.
  {
    key: 'goals',
    index: 6,
    label: 'الأهداف',
    hint: 'هدف واحد على الأقل ليرتبط بالـ KPI ويُفتح توليد الاستراتيجية.',
  },
  { key: 'review', index: 7, label: 'المراجعة', hint: 'نظرة شاملة وإقرار اكتمال الملف.' },
];

const PRODUCTS_MAX = 10;

// Task 112A — every profile column the apply endpoint accepts.
// After extraction we send the FULL list with `overwrite_existing`
// false so the server itself decides which fields to write:
//   - `existing_value_protected` skip when the canonical row
//     already has a non-empty value (user-entered text is never
//     overwritten);
//   - `no_value_in_suggestion` skip when the worker emitted
//     nothing for that column (e.g. `weaknesses`).
// Keeping the whitelist on the client is the smallest change that
// avoids a per-field round-trip while still preserving the apply
// endpoint's safety checks. Mirrors the `PROFILE_FIELDS` array in
// `supabase/functions/apply-client-onboarding-suggestion/index.ts`.
const ALL_PROFILE_FIELDS: ReadonlyArray<ApplyProfileField> = [
  'arabic_description',
  'english_description',
  'vision',
  'mission',
  'values_text',
  'usp',
  'strengths',
  'weaknesses',
  'offers_notes',
];

function rangeIndexes(count: number): ReadonlyArray<number> {
  if (!Number.isFinite(count) || count <= 0) return [];
  return Array.from({ length: Math.floor(count) }, (_, i) => i);
}

// Task 165 — worker caps used as safe fallbacks when the extraction
// job's metadata doesn't carry the per-section counts. Matches the
// worker's MAX_PRODUCTS / MAX_AUDIENCES / MAX_COMPETITORS constants
// in `supabase/functions/_shared/ai/workers/onboarding-website-
// extract-v2.ts`. The apply EF treats out-of-range indices as
// `invalid_index` skips, so over-sending is harmless.
const WORKER_MAX_PRODUCTS = 20;
const WORKER_MAX_AUDIENCES = 10;
const WORKER_MAX_COMPETITORS = 10;

/**
 * Task 165 — read a non-negative integer count from a job row's
 * `metadata` blob. Falls back to `fallback` when the key is absent,
 * non-numeric, negative, or non-finite. Used by `onSucceeded` to
 * dimension the apply payload's `accepted_fields.{products,
 * audiences, competitors}` indices.
 */
function readMetadataCount(
  metadata: Record<string, unknown> | null,
  key: string,
  fallback: number,
): number {
  if (!metadata) return fallback;
  const raw = metadata[key];
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
    return Math.floor(raw);
  }
  return fallback;
}

// Task 164 — load-bearing UUID gate on `extractResult.suggestion_id`.
// The apply EF's `parseBody`
// (`supabase/functions/apply-client-onboarding-suggestion/index.ts`)
// rejects non-UUID `suggestion_id` with `validation_error` (HTTP 400)
// and `body.fields = { suggestion_id: ['suggestion_id must be a UUID'] }`.
// Pre-checking on the client lets us route a degenerate extract
// response into a clean Arabic apply_failed notice WITHOUT round-
// tripping a request that we already know the server will refuse.
const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Task 164 — translate any thrown error from
 * `applyClientOnboardingSuggestion` into a clean Arabic message that
 * is safe to render in the apply_failed banner. NEVER surfaces the
 * synthetic `KmiApiError` constructor string; the wire envelope's
 * `code` is the source of truth, and `body.fields` is logged to the
 * console for diagnosis but not surfaced to the user.
 */
function mapApplyErrorMessage(err: unknown): string {
  if (!isApiError(err)) {
    return 'تعذّر إلصاق نتائج النموذج بحقول العميل. حاول مجدداً.';
  }
  switch (err.code) {
    case 'validation_error':
      return 'نتائج النموذج لم تعبر تحقّق الخدمة. حاول الاستخراج مجدداً، وإن استمرت المشكلة عبّئ الحقول يدوياً.';
    case 'permission_denied':
      return 'صلاحيتك لا تسمح بإلصاق القيم بحقول العميل. اطلب من مستخدم لديه صلاحية client.manage.';
    case 'not_found':
      return 'لم نعد نجد نتائج الاستخراج. شغّل الاستخراج مجدداً.';
    case 'conflict':
      return 'تم تطبيق هذه النتائج مسبقاً. حدّث الصفحة لرؤية القيم في الحقول.';
    case 'rate_limited':
      return 'الخدمة مشغولة حالياً. حاول بعد دقيقة.';
    case 'entitlement_denied':
      return 'الباقة الحالية لا تتضمن تشغيل النموذج.';
    case 'unauthenticated':
      return 'انتهت الجلسة. سيتم إعادة تسجيل الدخول…';
    case 'internal_error':
    default:
      return 'تعذّر إلصاق نتائج النموذج بحقول العميل. حاول مجدداً بعد قليل.';
  }
}

/**
 * Task 164 — bounded structured summary of a non-API thrown value for
 * the diagnostic log. The raw `unknown` could be a network error, a
 * DOMException, or anything else with arbitrary `.message` / `.stack`
 * content; we capture only the constructor name and a length-capped
 * message string, and never the stack. Keeps the console log
 * structured and PII-free (per Codex Step A P2-1 / P2-2).
 */
function summarizeUnknownError(err: unknown): {
  readonly kind: 'network' | 'aborted' | 'unknown';
  readonly name: string;
  readonly message: string;
} {
  let kind: 'network' | 'aborted' | 'unknown' = 'unknown';
  if (isNetworkError(err)) kind = 'network';
  else if (err instanceof DOMException && err.name === 'AbortError') kind = 'aborted';

  let name = 'UnknownError';
  let message = '';
  if (err instanceof Error) {
    name = err.name || 'Error';
    message = typeof err.message === 'string' ? err.message.slice(0, 200) : '';
  }
  return { kind, name, message };
}

// Task 165 — `mapExtractErrorMessage` (Task 164) moved into
// `useOnboardingExtractionJob` as `mapStartErrorToArabic` +
// `mapJobErrorCodeToArabic`. Extract errors now reach the user
// through the hook's `phase.kind === 'failure'` path, never via the
// wizard's own state.

type LoadState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly client: ClientOverviewData['client'];
      readonly brand: BrandInfo | null;
      readonly profile: ClientOnboardingProfile | null;
      readonly audiences: ReadonlyArray<TargetAudience>;
      readonly goals: ReadonlyArray<ClientGoal>;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

type AutoApplyNotice =
  | { readonly kind: 'no_permission' }
  | { readonly kind: 'apply_failed'; readonly message: string };

interface CompletionMap {
  readonly basics: boolean;
  readonly identity: boolean;
  readonly market: boolean;
  readonly products: boolean;
  readonly competitors: boolean;
  readonly goals: boolean;
  readonly review: boolean;
}

export function OnboardingWizardPage() {
  const { clientId } = useParams();
  const { me } = useMeContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  // Task 066 + Task 163 + Task 165: autofill state lives at wizard
  // level so the extract trigger and its result banner are reachable
  // from any section. Task 163 removed the separate review panel —
  // the extract CTA is now the entire AI surface. Task 165 moved the
  // start/poll lifecycle into `useOnboardingExtractionJob` so the
  // wizard talks to the deployed async EF (Task 093) correctly while
  // staying compatible with the legacy sync EF in master.
  // All ai.run / client.manage gating still flows through
  // `canExtractWebsite` below; nothing about the backend contract
  // changed.
  // Task 112A — surface the outcome of the auto-apply that runs
  // immediately after extraction succeeds. The result drives a
  // small banner under the CTA so the user can see what landed in
  // the canonical fields and what the server skipped (existing
  // values are protected by default).
  const [autoApplyResult, setAutoApplyResult] =
    useState<ApplyClientOnboardingSuggestionData | null>(null);
  const [autoApplyNotice, setAutoApplyNotice] = useState<AutoApplyNotice | null>(null);
  // Task 163 — set of profile columns the auto-apply just populated.
  // Used to render a subtle "معبأ بالذكاء · راجع" pill next to those
  // fields so the user knows which values came from the model. Lives
  // in component state only — resets on the next refresh / save and
  // is never persisted.
  const [aiFilledProfile, setAiFilledProfile] = useState<ReadonlySet<ApplyProfileField>>(
    () => new Set(),
  );

  const canManage =
    !!me &&
    !me.system_admin &&
    hasAnyPermission(me.permissions, PERM.CLIENT_MANAGE, PERM.CLIENT_SELF_MANAGE);
  // Task 059: the website extractor backend gates on `ai.run` AND
  // `client.manage` (NOT `client.self.manage`) — see
  // `extract-client-onboarding-from-website/index.ts`. Mirroring
  // both perms here prevents the active-button → 403 round-trip
  // for a user who holds `client.manage` but not `ai.run`. Codex
  // round 1 (Task 066) caught the missing `AI_RUN` clause that
  // had been off since Task 059's frontend.
  const canExtractWebsite =
    !!me &&
    !me.system_admin &&
    hasAnyPermission(me.permissions, PERM.CLIENT_MANAGE) &&
    hasAnyPermission(me.permissions, PERM.AI_RUN);
  // Task 060 + Task 163: the apply endpoint gates on `client.manage`
  // ONLY — `ai.run` is NOT required (no AI call happens at apply
  // time). Auto-apply (Task 112A) calls it right after extraction,
  // so we still gate the auto-apply step on this perm. Conflating
  // it with `canExtractWebsite` would over-gate (a user with
  // `client.manage` but no `ai.run` can't extract anyway, but a user
  // with both can both extract AND auto-apply). `client.self.manage`
  // is intentionally NOT accepted — same posture as the backend gate
  // in `apply-client-onboarding-suggestion/index.ts`.
  const canApplySuggestions =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.CLIENT_MANAGE);

  // Task 165 — apply callback the extraction hook fires when the job
  // reaches a terminal `success` (either an async-EF polling success
  // or a `recently_succeeded` / legacy-sync short-circuit on start).
  // The hook has already UUID-validated `suggestionId` before calling
  // us; we re-validate here as defense-in-depth (Task 164 posture).
  //
  // Declared (and the hook is called) BEFORE any early returns so the
  // Rules of Hooks invariant — same hook order on every render — is
  // satisfied even when `state.status` flips through loading /
  // not-found / error (Codex Step A round 1 P1-B).
  async function onSucceeded(
    suggestionId: string,
    metadata: Record<string, unknown> | null,
  ): Promise<void> {
    if (!canApplySuggestions) {
      setAutoApplyNotice({ kind: 'no_permission' });
      return;
    }
    if (!UUID_REGEX.test(suggestionId)) {
      console.error(
        '[onboarding-extract] onSucceeded received non-UUID suggestionId (defense-in-depth)',
        { suggestion_id: suggestionId },
      );
      setAutoApplyNotice({
        kind: 'apply_failed',
        message:
          'تعذّر استخراج معرّف صالح للنتائج من الخدمة. حاول الاستخراج مجدداً، وإن استمرت المشكلة عبّئ الحقول يدوياً.',
      });
      return;
    }

    // Task 165 — dimension the apply call's accepted-fields indices
    // from the job row's `metadata.*_count` when present; fall back
    // to safe worker-cap upper bounds (matching the worker's
    // MAX_PRODUCTS / MAX_AUDIENCES / MAX_COMPETITORS). The apply EF
    // treats out-of-range indices as `invalid_index` skips, so
    // over-sending is harmless — profile fields apply regardless.
    const productsCount = readMetadataCount(metadata, 'products_count', WORKER_MAX_PRODUCTS);
    const audiencesCount = readMetadataCount(metadata, 'audiences_count', WORKER_MAX_AUDIENCES);
    const competitorsCount = readMetadataCount(
      metadata,
      'competitors_count',
      WORKER_MAX_COMPETITORS,
    );

    const applyInput = {
      suggestion_id: suggestionId,
      accepted_fields: {
        profile: ALL_PROFILE_FIELDS,
        products: rangeIndexes(productsCount),
        audiences: rangeIndexes(audiencesCount),
        competitors: rangeIndexes(competitorsCount),
      },
      overwrite_existing: false,
    };

    try {
      const applyResult = await applyClientOnboardingSuggestion({ input: applyInput });
      setAutoApplyResult(applyResult);
      setAiFilledProfile(new Set(applyResult.applied_fields.profile));
      // Refresh the canonical state so editable form fields hydrate
      // from the newly applied values.
      setReloadKey((n) => n + 1);
    } catch (applyErr) {
      // Task 164 — log the structured failure for diagnosis and
      // surface a clean Arabic message.
      if (isApiError(applyErr)) {
        console.error('[onboarding-extract] apply failed', {
          code: applyErr.code,
          status: applyErr.status,
          fields: applyErr.body.fields,
          request: applyInput,
        });
      } else {
        console.error('[onboarding-extract] apply failed (non-API error)', {
          error: summarizeUnknownError(applyErr),
          request: applyInput,
        });
      }
      setAutoApplyNotice({ kind: 'apply_failed', message: mapApplyErrorMessage(applyErr) });
    }
  }

  const extractionJob = useOnboardingExtractionJob({
    clientId: clientId ?? '',
    onSucceeded,
  });

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    Promise.all([
      getClientOverview(clientId, { signal: ctrl.signal }),
      getClientOnboardingProfile(clientId, { signal: ctrl.signal }).catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        if (isApiError(err) && (err.code === 'not_found' || err.code === 'permission_denied')) {
          return { onboarding_profile: null };
        }
        throw err;
      }),
    ])
      .then(([overview, onboarding]) => {
        if (ctrl.signal.aborted) return;
        setState({
          status: 'ready',
          client: overview.client,
          brand: overview.brand_info,
          profile: onboarding.onboarding_profile,
          audiences: overview.target_audiences,
          goals: overview.goals,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setState({ status: 'not-found' });
          return;
        }
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey]);

  if (!clientId) return null;

  if (state.status === 'loading') return <LoadingState title="جاري تحميل ملف التأهيل…" />;
  if (state.status === 'not-found')
    return (
      <NotFoundState
        title="لا يمكن الوصول إلى هذا العميل"
        description="إما أن العميل غير موجود، أو لا تملك صلاحية فتح ملفه."
      />
    );
  if (state.status === 'error')
    return (
      <ErrorState
        title="تعذر تحميل ملف التأهيل"
        error={state.error}
        onRetry={() => setReloadKey((n) => n + 1)}
      />
    );

  const completion = computeCompletion(state.brand, state.profile, state.audiences, state.goals);
  const completedCount = countCompleted(completion);
  // Task 162 — six fill steps: basics, identity, market, products,
  // competitors, goals (review is the attestation step, not a fill).
  const percent = Math.round((completedCount / 6) * 100);
  // Task 163 — manual section saves clear the "AI-filled" badges
  // across all sections (saving is the user's "I've reviewed this"
  // signal). The auto-apply path bumps `setReloadKey` directly so the
  // badges persist through the extraction → auto-apply → re-hydrate
  // cycle and only clear once the user explicitly saves a section.
  const refresh = () => {
    setAiFilledProfile(new Set());
    setReloadKey((n) => n + 1);
  };
  const onAudienceCreated = (audience: TargetAudience) => {
    setAiFilledProfile(new Set());
    setState((current) => {
      if (current.status !== 'ready') return current;
      if (current.audiences.some((item) => item.id === audience.id)) return current;
      return { ...current, audiences: [...current.audiences, audience] };
    });
    setReloadKey((n) => n + 1);
  };
  const onCompetitorsSaved = (competitors: ReadonlyArray<OnboardingCompetitorItem>) => {
    setAiFilledProfile(new Set());
    if (!state.profile) {
      refresh();
      return;
    }
    setState((current) => {
      if (current.status !== 'ready' || !current.profile) return current;
      return { ...current, profile: { ...current.profile, competitors } };
    });
  };
  // Task 088 Phase 1: the URL `?step=<key>` query param now scrolls to
  // the matching section anchor instead of switching which step
  // renders. We keep the param so existing deep links / "edit this
  // section" buttons elsewhere in the app still land in the right
  // place, just on a one-page workspace.
  const setStep = (key: StepKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('step', key);
    setSearchParams(next, { replace: true });
    if (typeof document !== 'undefined') {
      const el = document.getElementById(sectionAnchorId(key));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Task 066: wizard-level autofill trigger. The website URL is
  // saved on the `basics` step; until then the button is disabled
  // with explanatory copy. Permission gate (canExtractWebsite =
  // ai.run + client.manage + not sysadmin) is the existing Task 059
  // posture — unchanged.
  const websiteUrl = state.brand?.website_url ?? null;
  const hasWebsiteUrl = typeof websiteUrl === 'string' && websiteUrl.trim().length > 0;

  function onExtractClick() {
    if (!canExtractWebsite || !hasWebsiteUrl || extractionJob.isBusy) return;
    setAutoApplyResult(null);
    setAutoApplyNotice(null);
    setAiFilledProfile(new Set());
    void extractionJob.start();
  }

  return (
    <div className="onboarding-wizard">
      <WizardHeader
        clientName={state.client.name}
        clientId={clientId}
        percent={percent}
        completedCount={completedCount}
        totalSteps={STEPS.length - 1}
        canManage={canManage}
        completedAt={state.profile?.completed_at ?? null}
      />

      {/* Task 088 Phase 1: replaced the Stepper (which navigated
          between separate step pages) with a SectionTableOfContents
          (which scrolls within one page). Same data, same
          completion-state visualization — just no more "you're on
          step 2 of 6" mental model. */}
      <SectionTableOfContents completion={completion} onJumpTo={setStep} />

      {/* AI autofill CTA. Task 163 removed the separate
          suggestion-review panel — extraction now writes empty fields
          in place. Task 165 wired the start/poll lifecycle through
          `useOnboardingExtractionJob`; the banner consumes the
          hook's `phase` for the tracking / failure copy. */}
      {canExtractWebsite ? (
        <WizardAiAutofillBanner
          hasWebsiteUrl={hasWebsiteUrl}
          extractionPhase={extractionJob.phase}
          autoApplyResult={autoApplyResult}
          autoApplyNotice={autoApplyNotice}
          onExtractClick={onExtractClick}
        />
      ) : null}

      {/* Task 088 Phase 1: missing-fields summary moved above the
          sections stack so gaps are visible BEFORE the user starts
          scrolling. Each gap is a clickable chip that scrolls to the
          relevant section's anchor. Replaces the WizardFooter's
          "missing items" pills (which only appeared at the bottom). */}
      <MissingFieldsSummary completion={completion} onJumpTo={setStep} />

      {/* Task 088 Phase 1: every step component renders sequentially
          on the SAME page. No `currentStep` state, no advance
          behavior — `onSaved` only triggers a data refresh. The
          existing per-step save buttons keep working as-is; they
          just no longer navigate elsewhere. */}
      <div className="onboarding-wizard-content">
        <OnboardingSectionsStack
          clientId={clientId}
          brand={state.brand}
          profile={state.profile}
          audiences={state.audiences}
          goals={state.goals}
          aiFilledProfile={aiFilledProfile}
          canManage={canManage}
          onSaved={refresh}
          onAudienceCreated={onAudienceCreated}
          onCompetitorsSaved={onCompetitorsSaved}
          onJumpTo={setStep}
        />
      </div>
    </div>
  );
}

/**
 * Task 088 Phase 1 — sticky table of contents that replaces the
 * wizard stepper. Each item is an anchor link that scrolls to the
 * matching section on the same page. The completion checkmarks /
 * indices are preserved so the visual progress signal is identical
 * to the old stepper.
 */
function SectionTableOfContents({
  completion,
  onJumpTo,
}: {
  readonly completion: CompletionMap;
  readonly onJumpTo: (key: StepKey) => void;
}) {
  return (
    <nav className="onboarding-stepper" aria-label="فهرس أقسام الملف">
      {STEPS.map((s) => {
        const done = completion[s.key];
        const cls = ['onboarding-step'];
        if (done) cls.push('is-done');
        return (
          <button
            key={s.key}
            type="button"
            className={cls.join(' ')}
            onClick={() => onJumpTo(s.key)}
          >
            <span className="onboarding-step-index" aria-hidden="true">
              {done ? '✓' : String(s.index).padStart(2, '0')}
            </span>
            <span className="onboarding-step-text">
              <strong>{s.label}</strong>
              <span className="tiny">{s.hint}</span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Task 088 Phase 1 — alerts panel summarising what's still missing in
 * the foundation. Replaces the WizardFooter's bottom-of-page "missing
 * items" pills. Each pill is a scroll-to-section anchor.
 */
function MissingFieldsSummary({
  completion,
  onJumpTo,
}: {
  readonly completion: CompletionMap;
  readonly onJumpTo: (key: StepKey) => void;
}) {
  const missing = STEPS.filter((s) => s.key !== 'review' && !completion[s.key]);
  if (missing.length === 0) {
    return (
      <section
        className="banner banner-success onboarding-wizard-completion"
        aria-label="حالة اكتمال الملف"
        style={{ display: 'flex', alignItems: 'center', gap: 10 }}
      >
        <strong>كل الأقسام مكتملة.</strong>
        <span className="muted">انتقل إلى قسم "المراجعة والاعتماد" لإقفال الملف.</span>
      </section>
    );
  }
  return (
    <section
      className="banner banner-info onboarding-wizard-completion"
      aria-label="الحقول والأقسام الناقصة"
      style={{ display: 'grid', gap: 8 }}
    >
      <div>
        <strong>أقسام تحتاج إلى استكمال</strong>
        <p className="tiny" style={{ margin: '4px 0 0', color: 'var(--muted)' }}>
          الاستراتيجية تعتمد على متانة هذا الملف. أكمل العناصر التالية قبل تشغيل التوليد الذكي.
        </p>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {missing.map((m) => (
          <button
            key={m.key}
            type="button"
            className="pill pill-review"
            onClick={() => onJumpTo(m.key)}
            style={{ cursor: 'pointer', border: 'none' }}
          >
            {m.label}
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * Task 088 Phase 1 — renders ALL step components sequentially in one
 * page. Each component is wrapped in an anchored section so the
 * `?step=<key>` URL query param + the SectionTableOfContents +
 * MissingFieldsSummary buttons can scroll to the right place.
 *
 * Crucially, the `onSaved` callback passed down to each step now
 * does ONLY a data refresh — no `advanceFrom('basics')` /
 * `setStep('identity')`. The pre-Task-088 StepRouter chained `onSaved
 * → refresh → advance to next step`; with one page there's no "next
 * step" to advance to. The save buttons inside each step component
 * still work unchanged (they call their respective upsert API).
 */
function OnboardingSectionsStack({
  clientId,
  brand,
  profile,
  audiences,
  goals,
  aiFilledProfile,
  canManage,
  onSaved,
  onAudienceCreated,
  onCompetitorsSaved,
  onJumpTo,
}: {
  readonly clientId: string;
  readonly brand: BrandInfo | null;
  readonly profile: ClientOnboardingProfile | null;
  readonly audiences: ReadonlyArray<TargetAudience>;
  readonly goals: ReadonlyArray<ClientGoal>;
  readonly aiFilledProfile: ReadonlySet<ApplyProfileField>;
  readonly canManage: boolean;
  readonly onSaved: () => void;
  readonly onAudienceCreated: (audience: TargetAudience) => void;
  readonly onCompetitorsSaved: (competitors: ReadonlyArray<OnboardingCompetitorItem>) => void;
  readonly onJumpTo: (key: StepKey) => void;
}) {
  return (
    <>
      <div id={sectionAnchorId('basics')}>
        <BasicsStep clientId={clientId} brand={brand} canManage={canManage} onSaved={onSaved} />
      </div>
      <div id={sectionAnchorId('identity')}>
        <IdentityStep
          clientId={clientId}
          profile={profile}
          aiFilledProfile={aiFilledProfile}
          canManage={canManage}
          onSaved={onSaved}
        />
      </div>
      <div id={sectionAnchorId('market')}>
        <MarketStep
          clientId={clientId}
          profile={profile}
          audiences={audiences}
          aiFilledProfile={aiFilledProfile}
          canManage={canManage}
          onSaved={onSaved}
          onAudienceCreated={onAudienceCreated}
        />
      </div>
      <div id={sectionAnchorId('products')}>
        <ProductsStep
          clientId={clientId}
          profile={profile}
          canManage={canManage}
          onSaved={onSaved}
        />
      </div>
      <div id={sectionAnchorId('competitors')}>
        <CompetitorsStep
          clientId={clientId}
          profile={profile}
          canManage={canManage}
          onSaved={onCompetitorsSaved}
        />
      </div>
      <div id={sectionAnchorId('goals')}>
        <GoalsStep clientId={clientId} goals={goals} canManage={canManage} onSaved={onSaved} />
      </div>
      <div id={sectionAnchorId('review')}>
        <ReviewStep
          clientId={clientId}
          brand={brand}
          profile={profile}
          audiences={audiences}
          goals={goals}
          canManage={canManage}
          onSaved={onSaved}
          onJumpTo={onJumpTo}
        />
      </div>
    </>
  );
}

/**
 * Task 088 Phase 1 — produces a stable DOM id for a given section so
 * `setStep` can scroll to it via `document.getElementById`. Keeping
 * the prefix `onboarding-section-` namespaces the ids out of any
 * other on-page id collisions.
 */
function sectionAnchorId(key: StepKey): string {
  return `onboarding-section-${key}`;
}

/**
 * Task 066 + Task 112A: wizard-level CTA that asks the AI to draft
 * the remaining onboarding sections. Calls the EXISTING
 * `extract-client-onboarding-from-website` endpoint, then (Task
 * 112A) auto-applies the resulting suggestion into the canonical
 * onboarding rows with `overwrite_existing: false` so only empty
 * fields are populated. The button's visibility is gated by the
 * parent on `canExtractWebsite` (ai.run + client.manage + not
 * sysadmin); the disabled state inside the banner only handles the
 * "no website URL yet" + "extracting in progress" states.
 *
 * Honest copy:
 *   - the AI fills ONLY empty fields; existing values are kept;
 *   - to replace a saved value with the AI suggestion, the user
 *     edits the field manually in the form (Task 163 removed the
 *     separate review surface);
 *   - competitors are extracted only when the brand's own website
 *     names them explicitly — external competitor discovery is a
 *     separate, currently-unbuilt feature;
 *   - weaknesses are deliberately NOT extracted from public
 *     marketing copy.
 */
function WizardAiAutofillBanner({
  hasWebsiteUrl,
  extractionPhase,
  autoApplyResult,
  autoApplyNotice,
  onExtractClick,
}: {
  readonly hasWebsiteUrl: boolean;
  readonly extractionPhase: OnboardingExtractionPhase;
  readonly autoApplyResult: ApplyClientOnboardingSuggestionData | null;
  readonly autoApplyNotice: AutoApplyNotice | null;
  readonly onExtractClick: () => void;
}) {
  const isBusy = extractionPhase.kind === 'starting' || extractionPhase.kind === 'tracking';
  const disabled = !hasWebsiteUrl || isBusy;
  return (
    <section
      className="banner banner-info onboarding-wizard-autofill"
      aria-label="تعبئة بقية الخطوات بالذكاء الاصطناعي"
      style={{ display: 'grid', gap: 10 }}
    >
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <strong>تعبئة الحقول الفارغة بالذكاء الاصطناعي</strong>
          <p className="tiny" style={{ margin: '4px 0 0', color: 'var(--muted)' }}>
            {hasWebsiteUrl
              ? 'سنقرأ موقع العلامة ونعبّئ تلقائياً الحقول الفارغة فقط في الهوية والسوق والمنتجات والمنافسين. القيم التي كتبتها بنفسك تبقى محفوظة ولن نتجاوزها — راجع الحقول التي ستظهر بالأسفل وعدّلها قبل الاعتماد.'
              : 'أكمل خطوة "الأساسيات" وأضف رابط الموقع، ثم سيتفعّل الاقتراح الذكي للخطوات المتبقية.'}
          </p>
        </div>
        <button
          type="button"
          className="btn"
          disabled={disabled}
          onClick={onExtractClick}
          title={!hasWebsiteUrl ? 'أكمل خطوة الأساسيات أولاً وأضف رابط الموقع.' : undefined}
          style={{ minWidth: 168 }}
        >
          {isBusy ? 'جارٍ الاستخراج…' : 'استخرج من الموقع'}
        </button>
      </div>
      <ul
        className="tiny"
        style={{
          margin: 0,
          paddingInlineStart: 18,
          color: 'var(--muted)',
          display: 'grid',
          gap: 4,
        }}
      >
        <li>
          يُستخرج المنافسون فقط حين يذكرهم نص الموقع صراحة (مثل &laquo;بالشراكة مع&raquo; أو
          &laquo;بخلاف&raquo;). البحث في الويب الخارجي عن منافسين غير مفعّل حالياً، لذا قد تكون
          القائمة فارغة حتى لو كان للعلامة منافسون فعليون.
        </li>
        <li>
          نقاط الضعف لا يستخرجها النموذج عمداً من نصوص التسويق العامة — تُترك للتعبئة اليدوية.
        </li>
      </ul>
      {/* Task 165 — tracking copy. Distinct first-tick notice when the
          EF reported `already_running` so the user knows another
          session/tab is in flight; otherwise the generic progress
          message with the dual-minute estimate. */}
      {extractionPhase.kind === 'tracking' && extractionPhase.alreadyRunningNotice ? (
        <div className="banner banner-info" role="status" style={{ marginTop: 4 }}>
          الاستخراج جارٍ بالفعل، سنحدّث الحقول عند انتهاء المعالجة.
        </div>
      ) : null}
      {extractionPhase.kind === 'tracking' && !extractionPhase.alreadyRunningNotice ? (
        <div className="banner banner-info" role="status" style={{ marginTop: 4 }}>
          جاري استخراج بيانات الموقع… قد يستغرق ذلك دقيقة أو دقيقتين.
        </div>
      ) : null}
      {extractionPhase.kind === 'starting' ? (
        <div className="banner banner-info" role="status" style={{ marginTop: 4 }}>
          جاري بدء الاستخراج…
        </div>
      ) : null}
      {extractionPhase.kind === 'failure' ? (
        <div className="banner banner-error" role="alert" style={{ marginTop: 4 }}>
          {extractionPhase.errorMessage}
        </div>
      ) : null}
      {autoApplyResult ? (
        <AutoApplyResultBanner result={autoApplyResult} />
      ) : autoApplyNotice ? (
        <AutoApplyNoticeBanner notice={autoApplyNotice} />
      ) : null}
    </section>
  );
}

/**
 * Task 112A + Task 163 — short banner shown right after the auto-apply
 * pass succeeds. Reports the outcome in plain Arabic without surfacing
 * draft / confidence / suggestion-id metadata. The actual AI values
 * appear directly in the form fields below; this banner is just the
 * "review before approval" prompt.
 */
function AutoApplyResultBanner({
  result,
}: {
  readonly result: ApplyClientOnboardingSuggestionData;
}) {
  const totalApplied =
    result.applied_fields.profile.length +
    result.applied_fields.brand_info.length +
    result.applied_fields.products_added +
    result.applied_fields.competitors_added +
    result.applied_fields.audiences_added_ids.length;
  const protectedCount = result.skipped.filter((s) => s.code === 'existing_value_protected').length;
  if (totalApplied === 0 && protectedCount === 0) {
    return (
      <div className="banner banner-info" role="status" style={{ marginTop: 4 }}>
        لم يجد النموذج قيماً جديدة قابلة للتعبئة. عبّئ الحقول يدوياً أو راجع رابط الموقع وحاول
        مجدداً.
      </div>
    );
  }
  return (
    <div className="banner banner-success" role="status" style={{ marginTop: 4 }}>
      تمت تعبئة الحقول الفارغة من الموقع — راجع القيم في الأقسام بالأسفل قبل الاعتماد.
      {protectedCount > 0 ? ' قيم سبق إدخالها لم نتجاوزها — حدّثها يدوياً عند الحاجة.' : ''}
    </div>
  );
}

/**
 * Task 112A + Task 163 — informational banner for the two non-success
 * paths after extraction. Either the user lacks the apply permission
 * or the apply call itself failed. No mention of a separate review
 * surface — Task 163 removed it.
 */
function AutoApplyNoticeBanner({ notice }: { readonly notice: AutoApplyNotice }) {
  if (notice.kind === 'no_permission') {
    return (
      <div className="banner banner-info" role="status" style={{ marginTop: 4 }}>
        تم تشغيل النموذج، لكن إلصاق القيم بحقول العميل يحتاج صلاحية client.manage. اطلب من مستخدم
        لديه الصلاحية إعادة تشغيل الاستخراج.
      </div>
    );
  }
  return (
    <div className="banner banner-error" role="alert" style={{ marginTop: 4 }}>
      تعذّر إلصاق نتائج النموذج بحقول العميل: {notice.message} حاول مجدداً أو عبّئ الحقول يدوياً.
    </div>
  );
}

// ── header / stepper / footer ────────────────────────────────────────

function WizardHeader({
  clientName,
  clientId,
  percent,
  completedCount,
  totalSteps,
  canManage,
  completedAt,
}: {
  readonly clientName: string;
  readonly clientId: string;
  readonly percent: number;
  readonly completedCount: number;
  readonly totalSteps: number;
  readonly canManage: boolean;
  readonly completedAt: string | null;
}) {
  return (
    <header className="onboarding-wizard-header">
      <div>
        <span className="meta">ملف العميل</span>
        <h1 className="h-page" style={{ margin: '6px 0 4px' }}>
          ملف التأهيل
          <span
            className="serif italic"
            style={{ color: 'var(--tangerine)', fontSize: '0.55em', marginInlineStart: 8 }}
          >
            · {clientName}
          </span>
        </h1>
        <p className="muted" style={{ margin: 0 }}>
          هذا الملف هو الأساس الذي تُبنى عليه الاستراتيجية والمحتوى والحملات. أكمله مرّة واحدة بدقة،
          وارجع إليه عند أي تحديث جوهري.
        </p>
      </div>
      <div className="onboarding-wizard-progress">
        <ProgressRing percent={percent} />
        <div>
          <div className="lbl">التقدم</div>
          <strong style={{ fontSize: 18 }}>
            {completedCount} / {totalSteps}
          </strong>
          <div className="tiny" style={{ color: 'var(--muted)', marginTop: 4 }}>
            {completedAt
              ? `أُقرّ بالاكتمال ${new Date(completedAt).toLocaleDateString('ar-SA')}`
              : canManage
                ? 'لم يُقرّ بالاكتمال بعد.'
                : 'وضع القراءة فقط.'}
          </div>
          <Link
            to={`/app/clients/${clientId}/overview`}
            className="btn-text"
            style={{ padding: '6px 0', fontSize: 12, marginTop: 4 }}
          >
            ← عودة إلى نظرة العميل
          </Link>
        </div>
      </div>
    </header>
  );
}

function ProgressRing({ percent }: { readonly percent: number }) {
  const safe = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div
      className="onboarding-progress-ring"
      style={{
        // Inline gradient for the conic ring; rest in CSS.
        background: `conic-gradient(var(--tangerine) ${safe}%, var(--line) ${safe}% 100%)`,
      }}
      aria-label={`تقدم ${safe}٪`}
      role="img"
    >
      <div className="onboarding-progress-ring-inner">
        <strong>{safe}%</strong>
      </div>
    </div>
  );
}

// Task 088 Phase 1: removed `Stepper`, `WizardFooter`, and
// `StepRouter`. The single-page design replaces them with
// `SectionTableOfContents`, `MissingFieldsSummary`, and
// `OnboardingSectionsStack` (defined alongside the page component
// above). The step components themselves (BasicsStep, IdentityStep,
// MarketStep, ProductsStep, CompetitorsStep, ReviewStep) are
// unchanged — only the orchestration around them changed.

// ── Section: basics (brand info) ─────────────────────────────────────

function BasicsStep({
  clientId,
  brand,
  canManage,
  onSaved,
}: {
  readonly clientId: string;
  readonly brand: BrandInfo | null;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [brandName, setBrandName] = useState(brand?.brand_name ?? '');
  const [industry, setIndustry] = useState(brand?.industry ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(brand?.website_url ?? '');
  const [primaryLocale, setPrimaryLocale] = useState(brand?.primary_locale ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canManage) return;
    setError(null);
    setFieldErrors({});
    const trimmed = brandName.trim();
    if (trimmed.length === 0) {
      setFieldErrors({ brand_name: ['اسم العلامة مطلوب.'] });
      return;
    }
    setSubmitting(true);
    try {
      await upsertClientBrandInfo({
        input: {
          client_id: clientId,
          brand_name: trimmed,
          industry: industry.trim() || null,
          website_url: websiteUrl.trim() || null,
          primary_locale: primaryLocale.trim() || null,
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <StepShell
      title="الأساسيات"
      subtitle="هوية مختصرة للعلامة. يكفي ما يميّزها وما يدلّ على لغتها."
    >
      <form onSubmit={onSubmit} noValidate className="stack-md">
        {error ? <div className="banner banner-error">{error}</div> : null}
        <div className="onboarding-form-grid">
          <FormField
            id="brand_name"
            label="اسم العلامة"
            value={brandName}
            onChange={setBrandName}
            required
            maxLength={160}
            disabled={submitting || !canManage}
            error={fieldErrors['brand_name']?.[0]}
          />
          <FormField
            id="industry"
            label="القطاع"
            value={industry}
            onChange={setIndustry}
            maxLength={80}
            disabled={submitting || !canManage}
            placeholder="مثال: ضيافة"
            error={fieldErrors['industry']?.[0]}
          />
          <FormField
            id="website_url"
            label="الموقع"
            value={websiteUrl}
            onChange={setWebsiteUrl}
            maxLength={500}
            disabled={submitting || !canManage}
            placeholder="https://example.com"
            dirLtr
            error={fieldErrors['website_url']?.[0]}
          />
          <FormField
            id="primary_locale"
            label="اللغة الأساسية"
            value={primaryLocale}
            onChange={setPrimaryLocale}
            maxLength={16}
            disabled={submitting || !canManage}
            placeholder="ar-SA"
            dirLtr
            error={fieldErrors['primary_locale']?.[0]}
          />
        </div>
        <StepActions canManage={canManage} submitting={submitting} primaryLabel="حفظ والتالي" />
      </form>
    </StepShell>
  );
}

// ── Step 2: identity (vision/mission/values/usp/descriptions) ────────

function IdentityStep({
  clientId,
  profile,
  aiFilledProfile,
  canManage,
  onSaved,
}: {
  readonly clientId: string;
  readonly profile: ClientOnboardingProfile | null;
  readonly aiFilledProfile: ReadonlySet<ApplyProfileField>;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [arabicDescription, setArabicDescription] = useState(profile?.arabic_description ?? '');
  const [englishDescription, setEnglishDescription] = useState(profile?.english_description ?? '');
  const [vision, setVision] = useState(profile?.vision ?? '');
  const [mission, setMission] = useState(profile?.mission ?? '');
  const [valuesText, setValuesText] = useState(profile?.values_text ?? '');
  const [usp, setUsp] = useState(profile?.usp ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canManage) return;
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await upsertClientOnboardingProfile({
        input: {
          client_id: clientId,
          arabic_description: cleanText(arabicDescription),
          english_description: cleanText(englishDescription),
          vision: cleanText(vision),
          mission: cleanText(mission),
          values_text: cleanText(valuesText),
          usp: cleanText(usp),
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <StepShell
      title="الهوية والصوت"
      subtitle="ما الذي تقوله العلامة عن نفسها بالعربي والإنجليزي، وكيف تختلف عن غيرها."
    >
      <form onSubmit={onSubmit} noValidate className="stack-md">
        {error ? <div className="banner banner-error">{error}</div> : null}
        <div className="onboarding-form-grid">
          <FormField
            id="arabic_description"
            label="الوصف العربي"
            value={arabicDescription}
            onChange={setArabicDescription}
            textarea
            rows={5}
            maxLength={5000}
            disabled={submitting || !canManage}
            placeholder="وصف واضح للعلامة بالعربي…"
            error={fieldErrors['arabic_description']?.[0]}
            aiFilled={aiFilledProfile.has('arabic_description')}
          />
          <FormField
            id="english_description"
            label="الوصف الإنجليزي"
            value={englishDescription}
            onChange={setEnglishDescription}
            textarea
            rows={5}
            maxLength={5000}
            disabled={submitting || !canManage}
            placeholder="English brand description…"
            error={fieldErrors['english_description']?.[0]}
            aiFilled={aiFilledProfile.has('english_description')}
          />
          <FormField
            id="vision"
            label="الرؤية"
            value={vision}
            onChange={setVision}
            textarea
            rows={3}
            maxLength={2000}
            disabled={submitting || !canManage}
            error={fieldErrors['vision']?.[0]}
            aiFilled={aiFilledProfile.has('vision')}
          />
          <FormField
            id="mission"
            label="الرسالة"
            value={mission}
            onChange={setMission}
            textarea
            rows={3}
            maxLength={2000}
            disabled={submitting || !canManage}
            error={fieldErrors['mission']?.[0]}
            aiFilled={aiFilledProfile.has('mission')}
          />
          <FormField
            id="values_text"
            label="القيم"
            value={valuesText}
            onChange={setValuesText}
            textarea
            rows={3}
            maxLength={2000}
            disabled={submitting || !canManage}
            error={fieldErrors['values_text']?.[0]}
            aiFilled={aiFilledProfile.has('values_text')}
          />
          <FormField
            id="usp"
            label="نقطة البيع الفريدة"
            value={usp}
            onChange={setUsp}
            textarea
            rows={3}
            maxLength={2000}
            disabled={submitting || !canManage}
            error={fieldErrors['usp']?.[0]}
            aiFilled={aiFilledProfile.has('usp')}
          />
        </div>
        <StepActions canManage={canManage} submitting={submitting} primaryLabel="حفظ والتالي" />
      </form>
    </StepShell>
  );
}

// ── Step 3: market (audiences + SWOT-ish) ────────────────────────────

function MarketStep({
  clientId,
  profile,
  audiences,
  aiFilledProfile,
  canManage,
  onSaved,
  onAudienceCreated,
}: {
  readonly clientId: string;
  readonly profile: ClientOnboardingProfile | null;
  readonly audiences: ReadonlyArray<TargetAudience>;
  readonly aiFilledProfile: ReadonlySet<ApplyProfileField>;
  readonly canManage: boolean;
  readonly onSaved: () => void;
  readonly onAudienceCreated: (audience: TargetAudience) => void;
}) {
  const [strengths, setStrengths] = useState(profile?.strengths ?? '');
  const [weaknesses, setWeaknesses] = useState(profile?.weaknesses ?? '');
  const [offersNotes, setOffersNotes] = useState(profile?.offers_notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canManage) return;
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await upsertClientOnboardingProfile({
        input: {
          client_id: clientId,
          strengths: cleanText(strengths),
          weaknesses: cleanText(weaknesses),
          offers_notes: cleanText(offersNotes),
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <StepShell title="السوق" subtitle="من تخدم العلامة، ولماذا تختار العميل هذه العلامة على غيرها.">
      <div className="stack-md">
        <AudiencesPanel
          clientId={clientId}
          audiences={audiences}
          canManage={canManage}
          onSaved={onAudienceCreated}
        />

        <form onSubmit={onSubmit} noValidate className="stack-md">
          {error ? <div className="banner banner-error">{error}</div> : null}

          <div className="onboarding-subcard">
            <div className="sec-title">
              <h3>نقاط القوة والضعف والعروض</h3>
              <span className="meta">SWOT</span>
            </div>
            <div className="onboarding-form-grid">
              <FormField
                id="strengths"
                label="نقاط القوة"
                value={strengths}
                onChange={setStrengths}
                textarea
                rows={4}
                maxLength={2000}
                disabled={submitting || !canManage}
                error={fieldErrors['strengths']?.[0]}
                aiFilled={aiFilledProfile.has('strengths')}
              />
              <FormField
                id="weaknesses"
                label="نقاط الضعف"
                value={weaknesses}
                onChange={setWeaknesses}
                textarea
                rows={4}
                maxLength={2000}
                disabled={submitting || !canManage}
                error={fieldErrors['weaknesses']?.[0]}
                aiFilled={aiFilledProfile.has('weaknesses')}
              />
              <FormField
                id="offers_notes"
                label="العروض والمواسم"
                value={offersNotes}
                onChange={setOffersNotes}
                textarea
                rows={4}
                maxLength={2000}
                disabled={submitting || !canManage}
                error={fieldErrors['offers_notes']?.[0]}
                aiFilled={aiFilledProfile.has('offers_notes')}
              />
            </div>
          </div>

          <StepActions canManage={canManage} submitting={submitting} primaryLabel="حفظ والتالي" />
        </form>
      </div>
    </StepShell>
  );
}

function AudiencesPanel({
  clientId,
  audiences,
  canManage,
  onSaved,
}: {
  readonly clientId: string;
  readonly audiences: ReadonlyArray<TargetAudience>;
  readonly canManage: boolean;
  readonly onSaved: (audience: TargetAudience) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  return (
    <div className="onboarding-subcard">
      <div className="sec-title">
        <h3>الجمهور المستهدف</h3>
        <span className="meta">{audiences.length}</span>
      </div>
      {audiences.length === 0 ? (
        <p className="muted" style={{ margin: '0 0 12px' }}>
          لا يوجد جمهور معرّف بعد. أضف قطاعاً واحداً على الأقل قبل اعتماد التأهيل.
        </p>
      ) : (
        <ul className="onboarding-audience-list">
          {audiences.map((a) => (
            <li key={a.id}>
              <span style={{ fontWeight: 500 }}>{a.label}</span>
              {a.is_primary ? <span className="pill pill-active">رئيسي</span> : null}
            </li>
          ))}
        </ul>
      )}
      {canManage ? (
        showForm ? (
          <AudienceInlineForm
            clientId={clientId}
            onSaved={(audience) => {
              setShowForm(false);
              onSaved(audience);
            }}
            onCancel={() => setShowForm(false)}
          />
        ) : (
          <button
            type="button"
            className="btn-text"
            onClick={() => setShowForm(true)}
            style={{ padding: '8px 12px' }}
          >
            + إضافة جمهور
          </button>
        )
      ) : null}
      {canManage ? (
        <p className="tiny" style={{ marginTop: 10, color: 'var(--muted)' }}>
          تعديل / حذف قطاعات الجمهور يحتاج إلى edge function للتحديث والأرشفة لم يُبنَ بعد. حالياً
          يمكن الإضافة فقط.
        </p>
      ) : null}
    </div>
  );
}

function AudienceInlineForm({
  clientId,
  onSaved,
  onCancel,
}: {
  readonly clientId: string;
  readonly onSaved: (audience: TargetAudience) => void;
  readonly onCancel: () => void;
}) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    const trimmed = label.trim();
    if (trimmed.length === 0) {
      setFieldErrors({ label: ['تسمية الجمهور مطلوبة.'] });
      return;
    }
    setSubmitting(true);
    try {
      const result = await createClientTargetAudience({
        input: {
          client_id: clientId,
          label: trimmed,
          description: description.trim() || null,
          is_primary: isPrimary,
        },
      });
      onSaved(result.audience);
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="onboarding-audience-form">
      {error ? (
        <div className="banner banner-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}
      <FormField
        id="audience_label"
        label="تسمية الجمهور"
        value={label}
        onChange={setLabel}
        required
        maxLength={120}
        disabled={submitting}
        placeholder="مثال: نساء 25-34 الرياض"
        inputRef={ref}
        error={fieldErrors['label']?.[0]}
      />
      <FormField
        id="audience_description"
        label="وصف (اختياري)"
        value={description}
        onChange={setDescription}
        maxLength={500}
        disabled={submitting}
        textarea
        rows={2}
        error={fieldErrors['description']?.[0]}
      />
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 8,
          fontSize: 13,
        }}
      >
        <input
          type="checkbox"
          checked={isPrimary}
          onChange={(e) => setIsPrimary(e.currentTarget.checked)}
          disabled={submitting}
        />
        <span>تعيين كجمهور رئيسي</span>
      </label>
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button type="submit" className="auth-cta" disabled={submitting} style={{ width: 'auto' }}>
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري الإضافة…' : 'إضافة'}</span>
        </button>
        <button
          type="button"
          className="btn-text"
          onClick={onCancel}
          disabled={submitting}
          style={{ padding: '10px 14px' }}
        >
          إلغاء
        </button>
      </div>
    </form>
  );
}

// ── Step 4: products ─────────────────────────────────────────────────

interface ProductDraft {
  readonly name: string;
  readonly description: string;
  readonly price_range: string;
}

interface CompetitorDraft {
  readonly name: string;
  readonly website_url: string;
  readonly notes: string;
}

const BLANK_PRODUCT: ProductDraft = { name: '', description: '', price_range: '' };
const BLANK_COMPETITOR: CompetitorDraft = { name: '', website_url: '', notes: '' };

function ProductsStep({
  clientId,
  profile,
  canManage,
  onSaved,
}: {
  readonly clientId: string;
  readonly profile: ClientOnboardingProfile | null;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const initialRows: ReadonlyArray<ProductDraft> = profile?.products_services.length
    ? profile.products_services.map((item) => ({
        name: item.name,
        description: item.description ?? '',
        price_range: item.price_range ?? '',
      }))
    : [BLANK_PRODUCT];
  const [products, setProducts] = useState<ReadonlyArray<ProductDraft>>(initialRows);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const filledCount = products.filter((p) => p.name.trim().length > 0).length;
  const atMax = products.length >= PRODUCTS_MAX;

  // The wizard-level AI extract trigger handles auto-applying empty
  // products into this step's draft via the canonical-state refresh
  // (Task 112A behavior). This step only owns the manual products
  // editor; Task 163 removed the separate review panel that used to
  // sit alongside it.

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canManage) return;
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await upsertClientOnboardingProfile({
        input: {
          client_id: clientId,
          products_services: normalizeProducts(products),
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <StepShell
      title="المنتجات والخدمات"
      subtitle={`أضف حتى ${PRODUCTS_MAX} منتجات أو خدمات. الاسم مطلوب لكل عنصر؛ الوصف ونطاق السعر اختياريّان.`}
    >
      <form onSubmit={onSubmit} noValidate className="stack-md">
        {error ? <div className="banner banner-error">{error}</div> : null}

        <div className="onboarding-subcard">
          <div className="sec-title">
            <h3>قائمة المنتجات</h3>
            <span className="meta">
              {filledCount} / {PRODUCTS_MAX}
            </span>
          </div>
          <div className="stack-sm">
            {products.map((row, index) => (
              <div
                key={`product-${index}`}
                className="row-card onboarding-line-grid onboarding-line-grid-products"
              >
                <FormField
                  id={`product_name_${index}`}
                  label="الاسم"
                  value={row.name}
                  onChange={(name) =>
                    setProducts((rows) => rows.map((r, i) => (i === index ? { ...r, name } : r)))
                  }
                  maxLength={200}
                  disabled={submitting || !canManage}
                />
                <FormField
                  id={`product_description_${index}`}
                  label="الوصف"
                  value={row.description}
                  onChange={(description) =>
                    setProducts((rows) =>
                      rows.map((r, i) => (i === index ? { ...r, description } : r)),
                    )
                  }
                  maxLength={1000}
                  disabled={submitting || !canManage}
                />
                <FormField
                  id={`product_price_${index}`}
                  label="نطاق السعر"
                  value={row.price_range}
                  onChange={(price_range) =>
                    setProducts((rows) =>
                      rows.map((r, i) => (i === index ? { ...r, price_range } : r)),
                    )
                  }
                  maxLength={200}
                  disabled={submitting || !canManage}
                />
                <button
                  type="button"
                  className="btn-text"
                  disabled={submitting || !canManage}
                  onClick={() =>
                    setProducts((rows) =>
                      rows.length <= 1 ? [BLANK_PRODUCT] : rows.filter((_, i) => i !== index),
                    )
                  }
                  style={{ alignSelf: 'end', padding: '10px 12px' }}
                >
                  حذف
                </button>
              </div>
            ))}
          </div>
          {fieldErrors['products_services']?.[0] ? (
            <div className="tiny" style={{ color: 'var(--rose)', marginTop: 8 }}>
              {fieldErrors['products_services'][0]}
            </div>
          ) : null}
          <button
            type="button"
            className="btn-text"
            disabled={submitting || !canManage || atMax}
            onClick={() => setProducts((rows) => [...rows, BLANK_PRODUCT])}
            style={{ marginTop: 12, padding: '8px 12px' }}
            title={atMax ? `الحد الأقصى ${PRODUCTS_MAX} منتجات` : undefined}
          >
            + إضافة منتج
          </button>
          {atMax ? (
            <p className="tiny" style={{ color: 'var(--muted)', marginTop: 8 }}>
              وصلت للحد الأقصى. احذف أحد المنتجات لإضافة منتج جديد.
            </p>
          ) : null}
        </div>

        <StepActions canManage={canManage} submitting={submitting} primaryLabel="حفظ والتالي" />
      </form>
    </StepShell>
  );
}

// ── Step 5: competitors ──────────────────────────────────────────────

function CompetitorsStep({
  clientId,
  profile,
  canManage,
  onSaved,
}: {
  readonly clientId: string;
  readonly profile: ClientOnboardingProfile | null;
  readonly canManage: boolean;
  readonly onSaved: (competitors: ReadonlyArray<OnboardingCompetitorItem>) => void;
}) {
  const initialRows: ReadonlyArray<CompetitorDraft> = profile?.competitors.length
    ? profile.competitors.map((item) => ({
        name: item.name,
        website_url: item.website_url ?? '',
        notes: item.notes ?? '',
      }))
    : [BLANK_COMPETITOR];
  const [competitors, setCompetitors] = useState<ReadonlyArray<CompetitorDraft>>(initialRows);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const filledCount = competitors.filter((c) => c.name.trim().length > 0).length;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canManage) return;
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      const normalisedCompetitors = normalizeCompetitors(competitors);
      await upsertClientOnboardingProfile({
        input: {
          client_id: clientId,
          competitors: normalisedCompetitors,
        },
      });
      onSaved(normalisedCompetitors);
      setSubmitting(false);
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <StepShell
      title="المنافسون"
      subtitle="قائمة المنافسين تُعبَّأ بشكل يدوي أو من استخراج موقع العلامة عندما يذكرهم نص الموقع صراحة. الاكتشاف في السوق الأوسع لم يُبنَ بعد."
    >
      <form onSubmit={onSubmit} noValidate className="stack-md">
        {error ? <div className="banner banner-error">{error}</div> : null}

        <div className="banner banner-info onboarding-import-banner">
          <strong>كيف تُملأ هذه القائمة؟</strong>
          <span>
            استخراج من الموقع (زر &laquo;استخرج من الموقع&raquo; أعلى الصفحة) يضيف المنافسين الذين
            يردون في نص موقع العلامة صراحة — مثل &laquo;بالشراكة مع X&raquo; أو &laquo;بخلاف
            Y&raquo;. اكتشاف منافسين من السوق الأوسع (البحث في الويب الخارجي عن منافسين في نفس
            القطاع) لم يُبنَ بعد، فأي منافسين تعرفهم خارج نص الموقع يُضافون يدوياً هنا.
          </span>
          <span className="pill pill-paused" style={{ alignSelf: 'flex-start' }}>
            اكتشاف من السوق الأوسع — قريباً
          </span>
        </div>

        <div className="onboarding-subcard">
          <div className="sec-title">
            <h3>قائمة المنافسين</h3>
            <span className="meta">{filledCount}</span>
          </div>
          <div className="stack-sm">
            {competitors.map((row, index) => {
              // Task 168 — surface a `بحاجة تحقق` pill on rows that
              // have a non-empty name but no website URL. Helps the
              // user spot competitors (manual OR AI-extracted) whose
              // source can't be verified at a glance. Display-only;
              // the persistence shape is unchanged.
              //
              // Task 169 — also fire the pill when the URL is present
              // but unverifiable (placeholder domains like example.com
              // or any RFC 2606 reserved address). `isVerifiableCompetitorUrl`
              // returns false for empty / placeholder / invalid strings,
              // so the row card surfaces the warning regardless of
              // whether the bad URL came from manual entry or an AI
              // extraction round-trip.
              const needsVerification =
                row.name.trim().length > 0 && !isVerifiableCompetitorUrl(row.website_url);
              return (
                <div
                  key={`competitor-${index}`}
                  className="row-card onboarding-line-grid onboarding-line-grid-competitors"
                >
                  {needsVerification ? (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <span
                        className="pill pill-paused"
                        title="لا يوجد رابط موقع لهذا المنافس — تحقّق يدوياً قبل الاعتماد."
                      >
                        بحاجة تحقق
                      </span>
                    </div>
                  ) : null}
                  <FormField
                    id={`competitor_name_${index}`}
                    label="الاسم"
                    value={row.name}
                    onChange={(name) =>
                      setCompetitors((rows) =>
                        rows.map((r, i) => (i === index ? { ...r, name } : r)),
                      )
                    }
                    maxLength={200}
                    disabled={submitting || !canManage}
                  />
                  <FormField
                    id={`competitor_url_${index}`}
                    label="الموقع"
                    value={row.website_url}
                    onChange={(website_url) =>
                      setCompetitors((rows) =>
                        rows.map((r, i) => (i === index ? { ...r, website_url } : r)),
                      )
                    }
                    maxLength={500}
                    disabled={submitting || !canManage}
                    placeholder="https://example.com"
                    dirLtr
                  />
                  <FormField
                    id={`competitor_notes_${index}`}
                    label="ملاحظات"
                    value={row.notes}
                    onChange={(notes) =>
                      setCompetitors((rows) =>
                        rows.map((r, i) => (i === index ? { ...r, notes } : r)),
                      )
                    }
                    maxLength={1000}
                    disabled={submitting || !canManage}
                  />
                  <button
                    type="button"
                    className="btn-text"
                    disabled={submitting || !canManage}
                    onClick={() =>
                      setCompetitors((rows) =>
                        rows.length <= 1 ? [BLANK_COMPETITOR] : rows.filter((_, i) => i !== index),
                      )
                    }
                    style={{ alignSelf: 'end', padding: '10px 12px' }}
                  >
                    حذف
                  </button>
                </div>
              );
            })}
          </div>
          {fieldErrors['competitors']?.[0] ? (
            <div className="tiny" style={{ color: 'var(--rose)', marginTop: 8 }}>
              {fieldErrors['competitors'][0]}
            </div>
          ) : null}
          <button
            type="button"
            className="btn-text"
            disabled={submitting || !canManage}
            onClick={() => setCompetitors((rows) => [...rows, BLANK_COMPETITOR])}
            style={{ marginTop: 12, padding: '8px 12px' }}
          >
            + إضافة منافس
          </button>
        </div>

        <StepActions canManage={canManage} submitting={submitting} primaryLabel="حفظ والتالي" />
      </form>
    </StepShell>
  );
}

// ── Step 6: goals (Task 162) ─────────────────────────────────────────
//
// Foundation goals were previously edited on Overview via `GoalsSection`
// + `GoalForm`. Task 162 moves the editing surface here because audience
// + goals are the inputs Strategy/AI reads (see `enforceReadiness` in
// `create-client-strategy-from-onboarding/index.ts` and
// `generate-client-strategy-ai/index.ts`). Reuses the existing
// `createClientGoal` endpoint and `ClientGoal` type — no backend
// change.

function GoalsStep({
  clientId,
  goals,
  canManage,
  onSaved,
}: {
  readonly clientId: string;
  readonly goals: ReadonlyArray<ClientGoal>;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [showForm, setShowForm] = useState(false);

  return (
    <StepShell
      title="الأهداف"
      subtitle="أضف هدفاً واحداً على الأقل ليرتبط بمؤشرات الأداء وتعتمد عليه الاستراتيجية والتوليد الذكي."
    >
      <div className="stack-md">
        <div className="onboarding-subcard">
          <div className="sec-title">
            <h3>قائمة الأهداف</h3>
            <span className="meta">{goals.length}</span>
          </div>
          {goals.length === 0 ? (
            <p className="muted" style={{ margin: '0 0 12px' }}>
              لا يوجد أهداف بعد. أضف هدفاً واحداً على الأقل قبل توليد الاستراتيجية.
            </p>
          ) : (
            <ul className="onboarding-audience-list">
              {goals.map((g) => (
                <li key={g.id}>
                  <span style={{ fontWeight: 500 }}>{g.goal}</span>
                  <span className="tiny" style={{ color: 'var(--muted)', marginInlineStart: 8 }}>
                    {g.metric_name ? g.metric_name : 'بدون مؤشر'} ·{' '}
                    {g.target_value ?? 'بدون قيمة مستهدفة'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {canManage ? (
            showForm ? (
              <GoalInlineForm
                clientId={clientId}
                onSaved={() => {
                  setShowForm(false);
                  onSaved();
                }}
                onCancel={() => setShowForm(false)}
              />
            ) : (
              <button
                type="button"
                className="btn-text"
                onClick={() => setShowForm(true)}
                style={{ padding: '8px 12px' }}
              >
                + إضافة هدف
              </button>
            )
          ) : null}
          {canManage ? (
            <p className="tiny" style={{ marginTop: 10, color: 'var(--muted)' }}>
              تعديل / حذف الأهداف يحتاج إلى edge function للتحديث والأرشفة لم يُبنَ بعد. حالياً يمكن
              الإضافة فقط.
            </p>
          ) : null}
        </div>
      </div>
    </StepShell>
  );
}

function GoalInlineForm({
  clientId,
  onSaved,
  onCancel,
}: {
  readonly clientId: string;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}) {
  const [goal, setGoal] = useState('');
  const [metric, setMetric] = useState('');
  const [target, setTarget] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    const trimmed = goal.trim();
    if (trimmed.length === 0) {
      setFieldErrors({ goal: ['نص الهدف مطلوب.'] });
      return;
    }
    setSubmitting(true);
    try {
      await createClientGoal({
        input: {
          client_id: clientId,
          goal: trimmed,
          metric_name: metric.trim() || null,
          target_value: target.trim() || null,
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="onboarding-audience-form">
      {error ? (
        <div className="banner banner-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}
      <FormField
        id="goal_text"
        label="الهدف"
        value={goal}
        onChange={setGoal}
        required
        maxLength={200}
        disabled={submitting}
        placeholder="مثال: زيادة الوصول الشهري بنسبة 30%"
        inputRef={ref}
        error={fieldErrors['goal']?.[0]}
      />
      <div className="onboarding-form-grid">
        <FormField
          id="goal_metric"
          label="المؤشر (اختياري)"
          value={metric}
          onChange={setMetric}
          maxLength={120}
          disabled={submitting}
          placeholder="مثال: متوسط ROAS"
          error={fieldErrors['metric_name']?.[0]}
        />
        <FormField
          id="goal_target"
          label="القيمة المستهدفة (اختياري)"
          value={target}
          onChange={setTarget}
          maxLength={120}
          disabled={submitting}
          placeholder="مثال: 4.5×"
          error={fieldErrors['target_value']?.[0]}
        />
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button type="submit" className="auth-cta" disabled={submitting} style={{ width: 'auto' }}>
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري الإضافة…' : 'إضافة'}</span>
        </button>
        <button
          type="button"
          className="btn-text"
          onClick={onCancel}
          disabled={submitting}
          style={{ padding: '10px 14px' }}
        >
          إلغاء
        </button>
      </div>
    </form>
  );
}

// ── Step 7: review ───────────────────────────────────────────────────

function ReviewStep({
  clientId,
  brand,
  profile,
  audiences,
  goals,
  canManage,
  onSaved,
  onJumpTo,
}: {
  readonly clientId: string;
  readonly brand: BrandInfo | null;
  readonly profile: ClientOnboardingProfile | null;
  readonly audiences: ReadonlyArray<TargetAudience>;
  readonly goals: ReadonlyArray<ClientGoal>;
  readonly canManage: boolean;
  readonly onSaved: () => void;
  readonly onJumpTo: (key: StepKey) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const completion = useMemo(
    () => computeCompletion(brand, profile, audiences, goals),
    [brand, profile, audiences, goals],
  );
  const allFilled =
    completion.basics &&
    completion.identity &&
    completion.market &&
    completion.products &&
    completion.competitors &&
    completion.goals;
  const completedAt = profile?.completed_at ?? null;

  async function markComplete() {
    if (!canManage) return;
    setError(null);
    setSubmitting(true);
    try {
      await upsertClientOnboardingProfile({
        input: {
          client_id: clientId,
          completed_at: new Date().toISOString(),
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      if (err instanceof KmiApiError) {
        if (err.code === 'permission_denied') {
          setError('لا تملك صلاحية إقرار اكتمال التأهيل.');
          return;
        }
      }
      setError('تعذّر حفظ الاكتمال. حاول مجدداً.');
    }
  }

  async function clearCompletion() {
    if (!canManage) return;
    setError(null);
    setSubmitting(true);
    try {
      await upsertClientOnboardingProfile({
        input: { client_id: clientId, completed_at: null },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      if (err instanceof KmiApiError && err.code === 'permission_denied') {
        setError('لا تملك صلاحية تعديل التأهيل.');
        return;
      }
      setError('تعذّر تحديث الحالة. حاول مجدداً.');
    }
  }

  return (
    <StepShell
      title="المراجعة"
      subtitle="نظرة شاملة على ما تم تعبئته. أقرّ بالاكتمال متى ما اطمأننت للبيانات."
    >
      <div className="stack-md">
        {error ? <div className="banner banner-error">{error}</div> : null}

        <div className="onboarding-review-grid">
          <ReviewBlock
            label="الأساسيات"
            done={completion.basics}
            onJump={canManage ? () => onJumpTo('basics') : undefined}
          >
            <ReviewLine k="الاسم" v={brand?.brand_name} />
            <ReviewLine k="القطاع" v={brand?.industry} />
            <ReviewLine k="الموقع" v={brand?.website_url} />
            <ReviewLine k="اللغة الأساسية" v={brand?.primary_locale} />
          </ReviewBlock>

          <ReviewBlock
            label="الهوية والصوت"
            done={completion.identity}
            onJump={canManage ? () => onJumpTo('identity') : undefined}
          >
            <ReviewLine k="الوصف العربي" v={profile?.arabic_description} multiline />
            <ReviewLine k="الوصف الإنجليزي" v={profile?.english_description} multiline />
            <ReviewLine k="الرؤية" v={profile?.vision} multiline />
            <ReviewLine k="الرسالة" v={profile?.mission} multiline />
            <ReviewLine k="القيم" v={profile?.values_text} multiline />
            <ReviewLine k="USP" v={profile?.usp} multiline />
          </ReviewBlock>

          <ReviewBlock
            label="السوق"
            done={completion.market}
            onJump={canManage ? () => onJumpTo('market') : undefined}
          >
            <ReviewLine
              k="الجمهور"
              v={audiences.length === 0 ? null : audiences.map((a) => a.label).join('، ')}
            />
            <ReviewLine k="نقاط القوة" v={profile?.strengths} multiline />
            <ReviewLine k="نقاط الضعف" v={profile?.weaknesses} multiline />
            <ReviewLine k="العروض والمواسم" v={profile?.offers_notes} multiline />
          </ReviewBlock>

          <ReviewBlock
            label="المنتجات"
            done={completion.products}
            onJump={canManage ? () => onJumpTo('products') : undefined}
          >
            {profile?.products_services.length ? (
              <ul className="onboarding-review-list">
                {profile.products_services.map((p, i) => (
                  <li key={`${p.name}-${i}`}>
                    <strong>{p.name}</strong>
                    {p.description ? <span className="muted"> · {p.description}</span> : null}
                    {p.price_range ? <span className="tiny"> · {p.price_range}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="muted">لم تُسجّل منتجات.</span>
            )}
          </ReviewBlock>

          <ReviewBlock
            label="المنافسون"
            done={completion.competitors}
            onJump={canManage ? () => onJumpTo('competitors') : undefined}
          >
            {profile?.competitors.length ? (
              <ul className="onboarding-review-list">
                {profile.competitors.map((c, i) => (
                  <li key={`${c.name}-${i}`}>
                    <strong>{c.name}</strong>
                    {isVerifiableCompetitorUrl(c.website_url) ? (
                      <span className="tiny" dir="ltr">
                        {' · '}
                        {c.website_url}
                      </span>
                    ) : (
                      // Task 168 — competitor without a website URL: flag
                      // it for verification. Covers both manually-entered
                      // URL-less rows AND AI-extracted competitors (the
                      // apply EF currently drops the worker's `website_url`
                      // — separate backend follow-up; the badge stays
                      // useful after that fix lands too).
                      //
                      // Task 169 — also fires for non-empty but
                      // unverifiable URLs (e.g. `https://example.com`,
                      // `*.test`, bare `localhost`). Never render a
                      // placeholder domain as if it were a real source;
                      // surface the pill so the user knows to verify.
                      <span
                        className="pill pill-paused"
                        style={{ marginInlineStart: 8, fontWeight: 400, fontSize: 11 }}
                        title="لا يوجد رابط موقع موثوق لهذا المنافس — تحقّق يدوياً قبل الاعتماد."
                      >
                        بحاجة تحقق
                      </span>
                    )}
                    {c.notes ? <span className="muted"> · {c.notes}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="muted">لم يُسجّل منافسون.</span>
            )}
          </ReviewBlock>

          <ReviewBlock
            label="الأهداف"
            done={completion.goals}
            onJump={canManage ? () => onJumpTo('goals') : undefined}
          >
            {goals.length ? (
              <ul className="onboarding-review-list">
                {goals.map((g) => (
                  <li key={g.id}>
                    <strong>{g.goal}</strong>
                    <span className="muted">
                      {' · '}
                      {g.metric_name ? g.metric_name : 'بدون مؤشر'} ·{' '}
                      {g.target_value ?? 'بدون قيمة مستهدفة'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="muted">لم تُسجّل أهداف.</span>
            )}
          </ReviewBlock>
        </div>

        <div className="onboarding-review-cta">
          <div>
            <strong style={{ fontSize: 15 }}>
              {completedAt
                ? `أُقرّ بالاكتمال في ${new Date(completedAt).toLocaleDateString('ar-SA')}`
                : allFilled
                  ? 'كل الأقسام مكتملة. هل تقرّ باكتمال التأهيل؟'
                  : 'لا تزال هناك أقسام ناقصة.'}
            </strong>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              إقرار الاكتمال لا يُفعّل العميل وحده؛ التفعيل يتم من زر &laquo;إنهاء الإعداد وتفعيل
              العميل&raquo; في تاب نظرة عامة.
            </p>
          </div>
          {canManage ? (
            completedAt ? (
              <button
                type="button"
                className="btn-text"
                onClick={clearCompletion}
                disabled={submitting}
                style={{ padding: '10px 14px' }}
              >
                تراجع عن الإقرار
              </button>
            ) : (
              <button
                type="button"
                className="auth-cta"
                onClick={markComplete}
                disabled={submitting || !allFilled}
                style={{ width: 'auto' }}
                title={!allFilled ? 'أكمل كل الأقسام أولاً.' : undefined}
              >
                {submitting ? <span className="spinner" /> : null}
                <span>{submitting ? 'جاري الحفظ…' : 'إقرار اكتمال التأهيل'}</span>
              </button>
            )
          ) : (
            <Link
              to={`/app/clients/${clientId}/overview`}
              className="btn-text"
              style={{ padding: '10px 14px' }}
            >
              عودة إلى نظرة العميل
            </Link>
          )}
        </div>
      </div>
    </StepShell>
  );
}

function ReviewBlock({
  label,
  done,
  onJump,
  children,
}: {
  readonly label: string;
  readonly done: boolean;
  readonly onJump?: () => void;
  readonly children: ReactNode;
}) {
  return (
    <div className={`onboarding-review-block${done ? ' is-done' : ''}`}>
      <div className="sec-title" style={{ marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, margin: 0 }}>{label}</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className={done ? 'pill pill-active' : 'pill pill-paused'}>
            {done ? 'مكتمل' : 'ناقص'}
          </span>
          {onJump ? (
            <button
              type="button"
              className="btn-text"
              onClick={onJump}
              style={{ padding: '4px 8px', fontSize: 12 }}
            >
              تعديل
            </button>
          ) : null}
        </div>
      </div>
      <div className="onboarding-review-block-body">{children}</div>
    </div>
  );
}

function ReviewLine({
  k,
  v,
  multiline,
}: {
  readonly k: string;
  readonly v: string | null | undefined;
  readonly multiline?: boolean;
}) {
  const has = typeof v === 'string' && v.trim().length > 0;
  return (
    <div className="onboarding-review-line">
      <span className="lbl">{k}</span>
      <span
        className={has ? 'body' : 'muted'}
        style={{ whiteSpace: multiline ? 'pre-wrap' : undefined }}
      >
        {has ? v : '—'}
      </span>
    </div>
  );
}

// ── Shared step shell + actions ──────────────────────────────────────

function StepShell({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="card onboarding-step-card">
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <p className="muted" style={{ margin: '6px 0 0' }}>
          {subtitle}
        </p>
      </div>
      {children}
    </section>
  );
}

function StepActions({
  canManage,
  submitting,
  primaryLabel,
}: {
  readonly canManage: boolean;
  readonly submitting: boolean;
  readonly primaryLabel: string;
}) {
  if (!canManage) {
    return (
      <p className="tiny" style={{ color: 'var(--muted)' }}>
        أنت في وضع القراءة. تعديل ملف التأهيل يتطلّب صلاحية client.manage أو client.self.manage.
      </p>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <button type="submit" className="auth-cta" disabled={submitting} style={{ width: 'auto' }}>
        {submitting ? <span className="spinner" /> : null}
        <span>{submitting ? 'جاري الحفظ…' : primaryLabel}</span>
      </button>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────

function computeCompletion(
  brand: BrandInfo | null,
  profile: ClientOnboardingProfile | null,
  audiences: ReadonlyArray<TargetAudience>,
  goals: ReadonlyArray<ClientGoal>,
): CompletionMap {
  const basics = !!brand && truthy(brand.brand_name);
  const identity =
    !!profile &&
    (truthy(profile.vision) ||
      truthy(profile.mission) ||
      truthy(profile.values_text) ||
      truthy(profile.usp) ||
      truthy(profile.arabic_description) ||
      truthy(profile.english_description));
  const market = audiences.length > 0;
  const products = !!profile && profile.products_services.length > 0;
  const competitors = !!profile && profile.competitors.length > 0;
  // Task 162 — goals readiness mirrors the server-side
  // `enforceReadiness` check in
  // `create-client-strategy-from-onboarding/index.ts`: at least one
  // `client_goals` row.
  const goalsDone = goals.length > 0;
  const review = !!profile && !!profile.completed_at;
  return { basics, identity, market, products, competitors, goals: goalsDone, review };
}

function countCompleted(c: CompletionMap): number {
  // Review not counted toward fill progress; it's an attestation, not a fill.
  return [c.basics, c.identity, c.market, c.products, c.competitors, c.goals].filter(Boolean)
    .length;
}

// Task 088 Phase 1: `firstIncompleteStep` used to pick the section
// the wizard would auto-open on mount. The single-page design
// renders every section together, so there is no "first incomplete
// step to open" — the MissingFieldsSummary at the top of the page
// shows ALL incomplete sections at once. Helper deleted.

function truthy(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function cleanText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

/**
 * Task 169 — verifiable-URL gate for competitor website fields.
 *
 * The wizard renders any non-empty `website_url` as a real source link.
 * That trusts the upstream chain too much: the V2 AI worker accepts
 * placeholder strings (its schema marks `website_url` required while
 * the soft parser passes any string through unfiltered), the deployed
 * apply EF may carry that value through (drift from master is
 * unverified), and the upsert EF's URL check only enforces http(s) +
 * WHATWG-parseable — `https://example.com` passes both. Result: the
 * wizard can display fake competitor sources that look real.
 *
 * This helper is the read-side + save-side gate. Return `false` means
 * the URL is missing or unverifiable — render the `بحاجة تحقق` pill
 * and DROP the field before persisting.
 *
 * Rejected:
 *   - null / undefined / empty / whitespace-only
 *   - any leading scheme that is not `http:` / `https:` (`mailto:`,
 *     `tel:`, `javascript:`, `data:`, `file:`, …) — caught BEFORE the
 *     `https://` fallback so it cannot be smuggled past the parser
 *   - any URL that does not parse as http(s)
 *   - bracketed IPv6 literals (`http://[::1]`, …) — not real
 *     competitor domains
 *   - host `example.com` / `example.org` / `example.net` (or any
 *     subdomain — `www.example.com`, `foo.example.org`, …), with or
 *     without the FQDN trailing dot
 *   - host with reserved-for-docs TLDs `.test` / `.invalid` /
 *     `.localhost` / `.example` (RFC 2606 + RFC 6761)
 *   - bare `localhost` / bare `example`
 *
 * Scheme-less input (`example.com`, `acme.io`) is accepted at the
 * input level: we tack on `https://` before parsing, so the deny list
 * still fires.
 *
 * Both renders (`CompetitorsStep`, `ReviewStep`) and the save path
 * (`normalizeCompetitors`) consume this. The competitor URL input's
 * `placeholder="https://example.com"` is unchanged — placeholders are
 * fine; only `value` is sanitized.
 *
 * Defense-in-depth on the backend (worker schema, apply EF, upsert
 * EF) is documented as Task 169 follow-ups; this PR is frontend-only.
 */
function isVerifiableCompetitorUrl(raw: string | null | undefined): boolean {
  if (typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return false;

  // Detect ANY leading URI scheme (with or without `://` — `mailto:`,
  // `tel:`, `javascript:`, `data:` etc. don't have the slashes) and
  // reject anything that isn't http(s) BEFORE the URL parser sees it.
  // Without this gate, `mailto:info@competitor.com` would get
  // `https://` prepended and parse as `https://mailto:info@…` —
  // a Codex Step A P2 finding.
  const schemeMatch = trimmed.match(/^([a-z][a-z0-9+.-]*):/i);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme !== 'http' && scheme !== 'https') return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(schemeMatch ? trimmed : `https://${trimmed}`);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;

  // Bracketed IPv6 literals (`http://[::1]`, etc.) are not real
  // competitor websites; reject up front. Public competitor sites use
  // registrable domains. (Codex Step A P2.)
  if (parsed.hostname.startsWith('[') || parsed.hostname.endsWith(']')) return false;

  // Canonicalise: lower-case + strip trailing dot(s). `URL.hostname`
  // preserves trailing dots, so without this `https://example.com.`
  // (a valid FQDN with the root-label dot) would slip the deny list
  // because `lastTwo` would be `"com."` instead of `"example.com"`.
  // Codex Step A P1.
  const host = parsed.hostname.toLowerCase().replace(/\.+$/, '');
  if (host.length === 0) return false;

  const labels = host.split('.');
  const tld = labels[labels.length - 1];
  // Reserved-for-docs / non-registrable TLDs (RFC 2606 + RFC 6761).
  // `.example` was missing from the original list — Codex Step A P1.
  if (tld === 'test' || tld === 'invalid' || tld === 'localhost' || tld === 'example') {
    return false;
  }

  if (labels.length >= 2) {
    const lastTwo = labels.slice(-2).join('.');
    if (lastTwo === 'example.com' || lastTwo === 'example.org' || lastTwo === 'example.net') {
      return false;
    }
  }

  if (labels.length === 1) {
    if (labels[0] === 'example' || labels[0] === 'localhost') return false;
  }

  return true;
}

function normalizeProducts(
  rows: ReadonlyArray<ProductDraft>,
): ReadonlyArray<OnboardingProductServiceItem> {
  return rows
    .map((row) => ({
      name: row.name.trim(),
      description: cleanText(row.description) ?? undefined,
      price_range: cleanText(row.price_range) ?? undefined,
    }))
    .filter((row) => row.name.length > 0)
    .slice(0, PRODUCTS_MAX);
}

function normalizeCompetitors(
  rows: ReadonlyArray<CompetitorDraft>,
): ReadonlyArray<OnboardingCompetitorItem> {
  return rows
    .map((row) => {
      const cleanedUrl = cleanText(row.website_url);
      // Task 169 — strip unverifiable URLs (placeholder domains, invalid
      // strings) BEFORE they hit upsert-client-onboarding-profile. The
      // EF replaces (not merges) `competitors` array members, so
      // omitting `website_url` here nulls the persisted value on the
      // next save — implicitly cleaning legacy `https://example.com`
      // rows without a migration.
      const verifiableUrl =
        cleanedUrl !== null && isVerifiableCompetitorUrl(cleanedUrl) ? cleanedUrl : undefined;
      return {
        name: row.name.trim(),
        website_url: verifiableUrl,
        notes: cleanText(row.notes) ?? undefined,
      };
    })
    .filter((row) => row.name.length > 0);
}

function handleApiErr(
  err: unknown,
  setError: (v: string | null) => void,
  setFieldErrors: (v: Record<string, string[]>) => void,
): void {
  if (err instanceof KmiApiError) {
    if (err.code === 'validation_error' && err.body.fields) {
      const next: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(err.body.fields)) next[k] = [...v];
      setFieldErrors(next);
      return;
    }
    if (err.code === 'permission_denied') {
      setError('لا تملك صلاحية تنفيذ هذه العملية.');
      return;
    }
    if (err.code === 'not_found') {
      setError('العميل غير متاح أو تمت أرشفته.');
      return;
    }
    if (err.code === 'conflict') {
      setError('هذا السجل موجود بالفعل.');
      return;
    }
    if (err.code === 'unauthenticated') {
      setError('انتهت الجلسة. سيتم إعادة تسجيل الدخول…');
      return;
    }
  }
  setError('تعذّر إكمال العملية. حاول مجدداً.');
}

interface FormFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly required?: boolean;
  readonly maxLength?: number;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly type?: 'text' | 'date';
  readonly dirLtr?: boolean;
  readonly textarea?: boolean;
  readonly rows?: number;
  readonly inputRef?: React.Ref<HTMLInputElement>;
  readonly error?: string;
  /**
   * Task 163 — when true, render a subtle "معبأ بالذكاء · راجع" pill
   * after the label so the user can see which fields were just
   * populated by the AI extractor. No draft id / confidence / source
   * surfaces anywhere; the badge clears when the user saves the
   * section (see `refresh` in `OnboardingWizardPage`).
   */
  readonly aiFilled?: boolean;
}

function FormField(props: FormFieldProps): ReactNode {
  const errorId = props.error ? `${props.id}_error` : undefined;
  return (
    <div className="auth-field" style={{ marginBottom: 0 }}>
      <label
        className="auth-field-label"
        htmlFor={props.id}
        style={props.aiFilled ? { display: 'flex', alignItems: 'center', gap: 8 } : undefined}
      >
        <span>
          {props.label}
          {props.required ? ' *' : ''}
        </span>
        {props.aiFilled ? (
          <span
            className="pill pill-review"
            style={{ fontWeight: 400, fontSize: 11 }}
            title="هذه القيمة معبّأة من النموذج — راجعها قبل الاعتماد."
          >
            معبأ بالذكاء · راجع
          </span>
        ) : null}
      </label>
      {props.textarea ? (
        <textarea
          id={props.id}
          className="auth-input"
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          placeholder={props.placeholder}
          disabled={props.disabled}
          maxLength={props.maxLength}
          rows={props.rows ?? 2}
          aria-invalid={props.error ? true : undefined}
          aria-describedby={errorId}
          style={{ paddingInlineStart: 18, resize: 'vertical' }}
        />
      ) : (
        <input
          ref={props.inputRef}
          id={props.id}
          className="auth-input"
          type={props.type ?? 'text'}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          placeholder={props.placeholder}
          disabled={props.disabled}
          maxLength={props.maxLength}
          dir={props.dirLtr ? 'ltr' : undefined}
          aria-invalid={props.error ? true : undefined}
          aria-describedby={errorId}
          style={{ paddingInlineStart: 18 }}
        />
      )}
      {props.error ? (
        <div id={errorId} className="tiny" style={{ color: 'var(--rose)', marginTop: 4 }}>
          {props.error}
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Test-only re-exports.
//
// Pure helpers consumed by vitest pins for Task 169 (and any future
// tests that need to exercise the verifiable-URL gate or the save-path
// normalizer without rendering the whole wizard). The `_*ForTests`
// naming convention matches the pattern established in Task 165's
// hook tests; production code imports the original symbols by their
// real names, so accidentally importing the `_ForTests` alias in app
// code is conspicuous in code review.
// ─────────────────────────────────────────────────────────────────────

export const _isVerifiableCompetitorUrlForTests = isVerifiableCompetitorUrl;
export const _normalizeCompetitorsForTests = normalizeCompetitors;
